import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { formatDate, toIstInputValue, istInputToBackend } from '../utils/datetime';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SchemaSelector from '../components/SchemaSelector';
import FormBuilder from '../components/FormBuilder';
import LoadingSpinner from '../components/LoadingSpinner';
import PaymentSettingsForm from '../components/PaymentSettingsForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ApiService } from '../utils/api';
import { DEFAULT_PUBLIC_SEARCH_FIELDS } from '../utils/formSchemaFields';
import { syncEventPasses } from '../utils/passes';
import PageBackgroundsEditor from '../components/PageBackgroundsEditor';
import LanSyncKeyPanel from '../components/LanSyncKeyPanel';
import { getCurrencySymbol } from '../utils/currency';
import { useToast } from '../components/Toast';
import { useAuth } from '../contexts/AuthContext';
import SearchBar from '../components/SearchBar';
import { IMAGE_LIMITS, resizeImage } from '../utils/imageResize';

import { 
  Plus, 
  Calendar, 
  Users, 
  Settings, 
  Copy, 
  ExternalLink,
  Edit,
  Trash2,
  CreditCard,
  CheckCircle,
  AlertCircle,
  MapPin,
  Link as LinkIcon,
  Search,
  MoreVertical,
  QrCode,
  Mail,
  Lock,
  Printer
} from 'lucide-react';
import GenerateLinkModal from '../components/GenerateLinkModal';
import QRScanPurposeManager from '../components/QRScanPurposeManager';
import EmailConfigModal from '../components/EmailConfigModal';
import LockerConfigManager from '../components/LockerConfigManager';
import { eventRef } from '../utils/eventRef';


const EventManagement = () => {
  const [uploadingScanLogo, setUploadingScanLogo] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null); // event object when editing
  const [editingPaymentSettings, setEditingPaymentSettings] = useState(null); // eventId when editing payment settings
  const [generatingLinkForEvent, setGeneratingLinkForEvent] = useState(null); // eventId when generating link
  const [editingQRPurposes, setEditingQRPurposes] = useState(null); // event object when editing QR purposes
  const [editingEmailConfig, setEditingEmailConfig] = useState(null); // event object when editing email config
  const [editingLockers, setEditingLockers] = useState(null); // event object when configuring lockers
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const { user, isAdmin, isEditor, isViewer } = useAuth();
  const navigate = useNavigate();

  const [newEvent, setNewEvent] = useState({
    name: '',
    description: '',
    metaData: '',
    eventType: 'free',
    capacity: 100,
    waitlistEnabled: false,
    price: 0,
    currency: 'INR',
    eventDate: '',
    eventEndDate: '',
    location: '',
    faceIdEnabled: false,
    rfIdEnabled: false,
    sendEmail: true,
    sendWhatsapp: true,
    offlineEnabled: false,
    lockerEnabled: false,
    enableCouponCode: false,
    allowSharedLockers: false,
    maxUsersPerLocker: 4,
    showConfirmationEmailButton: true,
    scanManualSearchEnabled: true,
    scanQrInputEnabled: true,
    scanShowLogo: false,
    showFormLogo: true,
    autoApproveOnline: false,
    autoApproveOnSpot: false,
    deskPublic: false,
    badgeShareIos: false,
    badgeShareAndroid: false,
    offlineBadgeImport: false,
    selfServiceEnabled: false,
    showOnWebsite: false,
    selfServiceAllowCancel: false,
    selfServiceEditUntil: null,
    showPrintProgress: true,
    onSpotAccess: 'LOGIN',
    registrationsClosed: false,
    onSpotRegistrationsClosed: false,
    // Badge design is now managed through the badge configuration system
    formSchema: [],
    selectedSchemaId: null
  });

  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();


  // Fetch form schemas
  const { data: formSchemasResponse } = useQuery({
    queryKey: ['formSchemas'],
    queryFn: () => ApiService.getFormSchemas(),
  });
  
  // Extract schemas from paginated response
  const formSchemas = Array.isArray(formSchemasResponse?.content) ? formSchemasResponse.content : [];


  // Fetch events from API
  const { data: eventsResponse, isLoading, error: eventsError } = useQuery({
    queryKey: ['events'],
    queryFn: () => ApiService.getEvents(0, 50), // Get first 50 events
  });

  // Extract events from paginated response and ensure it's an array
  const events = Array.isArray(eventsResponse?.content) ? eventsResponse.content : [];

  // Filter events based on search term
  const filteredEvents = events.filter(event => {
    if (!searchTerm) return true;
    return (event.name || '').toLowerCase().includes(searchTerm.trim().toLowerCase());
  });

  // Create event mutation
  const createEventMutation = useMutation({
    mutationFn: (eventData) => ApiService.createEvent(eventData),
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries(['events']);
      setIsCreating(false);
      setNewEvent({
        name: '',
        description: '',
        metaData: '',
        eventType: 'free',
        capacity: 100,
        waitlistEnabled: false,
        price: 0,
        currency: 'INR',
        eventDate: '',
        eventEndDate: '',
        location: '',
        faceIdEnabled: false,
        rfIdEnabled: false,
        sendEmail: true,
        sendWhatsapp: true,
        offlineEnabled: false,
        lockerEnabled: false,
    enableCouponCode: false,
        showConfirmationEmailButton: true,
        scanManualSearchEnabled: true,
        scanQrInputEnabled: true,
    scanShowLogo: false,
    showFormLogo: true,
    autoApproveOnline: false,
    autoApproveOnSpot: false,
    deskPublic: false,
    badgeShareIos: false,
    badgeShareAndroid: false,
    offlineBadgeImport: false,
    selfServiceEnabled: false,
    showOnWebsite: false,
    selfServiceAllowCancel: false,
    selfServiceEditUntil: null,
    showPrintProgress: true,
    onSpotAccess: 'LOGIN',
    registrationsClosed: false,
    onSpotRegistrationsClosed: false,
        // Badge design is now managed through the badge configuration system
        formSchema: [],
        selectedSchemaId: null
      });
      if ((variables?.passes?.length || variables?.groups?.length) && data?.eventId) {
        try {
          await syncEventPasses(ApiService, data.eventId, variables.passes, variables.groups);
        } catch (error) {
          showError(error.message || 'Event created, but its passes could not be saved');
        }
      }
      showSuccess('Event created successfully!');
    },
    onError: (error) => {
      showError('Failed to create event. Please try again.');
    }
  });

  // Update event mutation
  const cloneEventMutation = useMutation({
    mutationFn: (eventId) => ApiService.cloneEvent(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries(['events']);
      showSuccess('Event cloned. The copy has its own link and does not share registrations with the original.');
    },
    onError: (error) => showError(error.message || 'Could not clone the event'),
  });

  const updateEventMutation = useMutation({
    mutationFn: ({ eventId, eventData }) => ApiService.updateEvent(eventId, eventData),
    onSuccess: async (data, { eventId, eventData }) => {
      if (eventData?.passesLoaded) {
        try {
          await syncEventPasses(ApiService, eventId, eventData.passes || [], eventData.groups || []);
        } catch (error) {
          showError(error.message || 'Event saved, but its passes could not be updated');
        }
      }
      queryClient.invalidateQueries(['events']);
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      queryClient.invalidateQueries({ queryKey: ['eventPublic', eventId] });
      setEditingEvent(null);
      showSuccess('Event updated successfully!');
    },
    onError: (error) => {
      showError('Failed to update event. Please try again.');
    }
  });

  // Payment settings queries and mutations
  const { data: paymentSettingsMap } = useQuery({
    queryKey: ['eventPaymentSettings', events?.map(e => e.id)],
    queryFn: async () => {
      if (!events || events.length === 0) return {};
      
      const settingsMap = {};
      await Promise.all(
        events.map(async (event) => {
          try {
            const settings = await ApiService.getEventPaymentSettings(event.id);
            settingsMap[event.id] = settings;
          } catch (error) {
            settingsMap[event.id] = null;
          }
        })
      );
      return settingsMap;
    },
    enabled: !!events && events.length > 0,
  });

  const savePaymentSettingsMutation = useMutation({
    mutationFn: ({ eventId, settingsData }) => 
      ApiService.createOrUpdateEventPaymentSettings(eventId, settingsData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['eventPaymentSettings'] });
      showSuccess('Payment settings saved successfully!');
      setEditingPaymentSettings(null);
    },
    onError: (error) => {
      showError('Failed to save payment settings. Please try again.');
      console.error('Save payment settings error:', error);
    }
  });

  const handleCreateEvent = () => {
    if (!newEvent.name.trim()) {
      showError('Event name is required');
      return;
    }

    createEventMutation.mutate(newEvent);
  };

  const handleUpdateEvent = () => {
    if (!editingEvent?.name.trim()) {
      showError('Event name is required');
      return;
    }

    // Clean up formSchema - remove options/pricing from non-dropdown fields
    const cleanedFormSchema = editingEvent.formSchema?.map(field => {
      const cleanedField = { ...field };
      
      // Only keep options/pricing for dropdown and radio fields
      if (field.type !== 'dropdown' && field.type !== 'radio') {
        delete cleanedField.options;
        delete cleanedField.optionsWithPricing;
        delete cleanedField.optionWithBlackLists;
      }
      
      return cleanedField;
    });

    const cleanedEvent = {
      ...editingEvent,
      formSchema: cleanedFormSchema
    };


    updateEventMutation.mutate({
      eventId: editingEvent.id,
      eventData: cleanedEvent
    });
  };

  const handleEditEvent = (event) => {
    

    // Clean up formSchema when loading - remove options from non-dropdown fields
    const cleanedFormSchema = event.formSchema?.map(field => {
      // Deep clone the field to prevent reference sharing across fields
      const cleanedField = { 
        ...field,
        // Deep clone arrays at field level
        blackedListedQrPurposes: field.blackedListedQrPurposes ? [...field.blackedListedQrPurposes] : undefined
      };
      
      // Only keep options/pricing for dropdown and radio fields
      if (field.type !== 'dropdown' && field.type !== 'radio') {
        delete cleanedField.options;
        delete cleanedField.optionsWithPricing;
        delete cleanedField.optionWithBlackLists;
      } else {
        
        // For dropdown/radio fields, merge optionsWithPricing and optionWithBlackLists
        if (cleanedField.optionsWithPricing && cleanedField.optionWithBlackLists && cleanedField.optionWithBlackLists.length > 0) {
          const mergedOptions = cleanedField.optionsWithPricing.map(option => {
            // Find matching blacklist data for this option
            const blacklistData = cleanedField.optionWithBlackLists.find(
              bl => bl.label === option.label
            );
            return {
              ...option,
              blackListedQrPurposes: blacklistData?.blackListedQrPurposes ? [...blacklistData.blackListedQrPurposes] : []
            };
          });
        
          // CRITICAL: Create completely independent copies for each property
          cleanedField.optionsWithPricing = mergedOptions.map(opt => ({ 
            ...opt, 
            blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] 
          }));
          cleanedField.optionWithBlackLists = mergedOptions.map(opt => ({ 
            ...opt, 
            blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] 
          }));
          cleanedField.options = mergedOptions.map(opt => opt.label);
        } else if (cleanedField.optionWithBlackLists && cleanedField.optionWithBlackLists.length > 0) {
          // If only optionWithBlackLists exists, use it for both
          const baseOptions = cleanedField.optionWithBlackLists.map(option => ({
            label: option.label,
            price: option.price || 0,
            priceSchedule: option.priceSchedule || [],
            blackListedQrPurposes: option.blackListedQrPurposes ? [...option.blackListedQrPurposes] : []
          }));
          
          // Create independent copies
          cleanedField.optionsWithPricing = baseOptions.map(opt => ({ 
            ...opt, 
            blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] 
          }));
          cleanedField.optionWithBlackLists = baseOptions.map(opt => ({ 
            ...opt, 
            blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] 
          }));
          cleanedField.options = baseOptions.map(opt => opt.label);
          
        } else if (cleanedField.optionsWithPricing && cleanedField.optionsWithPricing.length > 0) {
          // If only optionsWithPricing exists, create independent copies
          cleanedField.optionsWithPricing = cleanedField.optionsWithPricing.map(opt => ({ 
            ...opt, 
            blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] 
          }));
          cleanedField.optionWithBlackLists = cleanedField.optionsWithPricing.map(opt => ({ 
            ...opt, 
            blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : [] 
          }));
          cleanedField.options = cleanedField.optionsWithPricing.map(opt => opt.label);
        } else if (cleanedField.options && cleanedField.options.length > 0) {
          // If only simple options exist, ensure it's a fresh copy
          cleanedField.options = [...cleanedField.options];
        } else {
          console.log('  ⚠️ No options data found');
        }
      }
      
      return cleanedField;
    });

    // Normalize eventType to lowercase (backend sends FREE/PAID/free/paid)
    const normalizedEventType = event.eventType?.toLowerCase() || 'free';


    
    // Debug: Check for reference sharing between fields
    const dropdownFields = cleanedFormSchema?.filter(f => f.type === 'dropdown' || f.type === 'radio') || [];

    
    dropdownFields.forEach((field, index) => {
      console.log(`\n📊 Field ${index + 1}: "${field.label}"`, {
        options: field.options,
        optionsWithPricingLength: field.optionsWithPricing?.length,
        optionWithBlackListsLength: field.optionWithBlackLists?.length,
        // Check if arrays are the same reference
        sameReference: field.optionsWithPricing === field.optionWithBlackLists,
        firstOption: field.optionsWithPricing?.[0]
      });
      
      // Check if this field shares references with any other field
      dropdownFields.forEach((otherField, otherIndex) => {
        if (index !== otherIndex) {
          const sharesOptionsWithPricing = field.optionsWithPricing === otherField.optionsWithPricing;
          const sharesOptionWithBlackLists = field.optionWithBlackLists === otherField.optionWithBlackLists;
          if (sharesOptionsWithPricing || sharesOptionWithBlackLists) {
            console.error(`❌ "${field.label}" shares array references with "${otherField.label}"!`, {
              sharesOptionsWithPricing,
              sharesOptionWithBlackLists
            });
          }
        }
      });
    });

    // Try to find matching schema by comparing field structure
    const matchingSchema = formSchemas.find(schema => {
      if (!schema.fields || !cleanedFormSchema) return false;
      if (schema.fields.length !== cleanedFormSchema.length) return false;
      
      // Compare field labels and types
      return schema.fields.every((schemaField, index) => {
        const eventField = cleanedFormSchema[index];
        return eventField && 
               schemaField.label === eventField.label && 
               schemaField.type === eventField.type;
      });
    });
    
    const detectedSchemaId = matchingSchema?.schemaId;


    setEditingEvent({
      ...event,
      formSchema: cleanedFormSchema,
      selectedSchemaId: detectedSchemaId, // Use detected schemaId instead of undefined
      eventDate: event.eventDate || '',
      eventEndDate: event.eventEndDate || '',
      metaData: event.metaData ?? '',
      eventType: normalizedEventType,
      location: event.location || '',
      passes: [],
      groups: [],
      passesLoaded: false,
    });
    ApiService.getTicketingConfig(event.id).then((config) => {
      const passes = (config?.passes || []).map((pass) => ({
        id: pass.id,
        name: pass.name,
        type: pass.type || 'PAID',
        price: pass.price ?? 0,
        currency: pass.currency || 'INR',
        description: pass.description || '',
        groupId: pass.groupId || '',
      }));
      const groups = (config?.groups || []).map((group) => ({
        id: group.id,
        name: group.name,
        type: group.type || 'PAID',
        description: group.description || '',
      }));
      setEditingEvent((prev) => (prev && prev.id === event.id && !prev.passesLoaded ? { ...prev, passes, groups, passesLoaded: true } : prev));
    }).catch(() => {});
  };


  const handleSchemaSelect = (schemaId) => {
    const selectedSchema = formSchemas.find(schema => schema.schemaId === schemaId);
    if (selectedSchema) {
      const collectOptionPrices = (fields = []) => {
        const prices = [];
        fields.forEach((field) => {
          (field.optionsWithPricing || []).forEach((option) => {
            if ((option.price || 0) > 0) {
              prices.push(option.price);
            }
            if (option.subFields?.length) {
              prices.push(...collectOptionPrices(option.subFields));
            }
          });
        });
        return prices;
      };

      const allPrices = collectOptionPrices(selectedSchema.fields || []).filter(price => price > 0);

      // Check if any field has pricing options
      const hasPaidOptions = allPrices.length > 0;
      
      // Calculate minimum price from all paid options
      let minPrice = 0;
      if (hasPaidOptions) {
        minPrice = allPrices.length > 0 ? Math.min(...allPrices) : 0;
      }
      
      // Deep clone the schema fields to prevent reference sharing
      const clonedFields = (selectedSchema.fields || []).map(field => {
        const clonedField = { 
          ...field,
          blackedListedQrPurposes: field.blackedListedQrPurposes ? [...field.blackedListedQrPurposes] : []
        };
        
        // Deep clone dropdown/radio options
        if (field.type === 'dropdown' || field.type === 'radio') {
          if (field.options) clonedField.options = [...field.options];
          if (field.optionsWithPricing) {
            clonedField.optionsWithPricing = field.optionsWithPricing.map(opt => ({
              ...opt,
              blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : []
            }));
          }
          if (field.optionWithBlackLists) {
            clonedField.optionWithBlackLists = field.optionWithBlackLists.map(opt => ({
              ...opt,
              blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : []
            }));
          }
        }
        
        return clonedField;
      });
      
      setNewEvent(prev => ({
        ...prev,
        selectedSchemaId: schemaId,
        formSchema: clonedFields,
        passes: JSON.parse(JSON.stringify(selectedSchema.passes || [])),
        groups: JSON.parse(JSON.stringify(selectedSchema.groups || [])),
        passesLoaded: true,
        formLogoUrl: selectedSchema.formLogoUrl,
        formBackgroundUrl: selectedSchema.formBackgroundUrl,
        eventType: hasPaidOptions ? 'paid' : 'free',
        price: hasPaidOptions ? minPrice : 0
      }));
      
      if (hasPaidOptions) {
        showSuccess(`Event automatically set to paid (min price: ₹${minPrice}) due to pricing in form schema`);
      }
    }
  };

  const handleCreateNewSchema = () => {
    // Navigate to form builder settings
    window.open('/admin/settings/forms', '_blank');
  };

  const resetForm = () => {
    setNewEvent({
      name: '',
      description: '',
      metaData: '',
      slug: '',
      eventType: 'free',
      capacity: 100,
      waitlistEnabled: false,
      price: 0,
      currency: 'INR',
      eventDate: '',
      eventEndDate: '',
      // Badge design is now managed through the badge configuration system
      formSchema: [],
      selectedSchemaId: null
    });
    setIsCreating(false);
  };

  const copyShareLink = (eventId) => {
    const link = `${window.location.origin}/register/${eventRef(eventId)}`;
    
    // Check if clipboard API is available
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link)
        .then(() => {
          showSuccess('Share link copied to clipboard!');
        })
        .catch((err) => {
          console.error('Failed to copy to clipboard:', err);
          fallbackCopyToClipboard(link);
        });
    } else {
      // Fallback for browsers that don't support clipboard API
      fallbackCopyToClipboard(link);
    }
  };

  const fallbackCopyToClipboard = (text) => {
    // Create a temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        showSuccess('Share link copied to clipboard!');
      } else {
        showError('Failed to copy link. Please copy manually: ' + text);
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      showError('Failed to copy link. Please copy manually: ' + text);
    }
    
    document.body.removeChild(textArea);
  };

  // Payment settings handlers
  const handleEditPaymentSettings = (eventId) => {
    setEditingPaymentSettings(eventId);
  };

  const handleSavePaymentSettings = (settingsData) => {
    if (editingPaymentSettings) {
      savePaymentSettingsMutation.mutate({
        eventId: editingPaymentSettings,
        settingsData: {
          ...settingsData,
          eventId: editingPaymentSettings,
          isGlobal: false
        }
      });
    }
  };

  const handleCancelPaymentSettings = () => {
    setEditingPaymentSettings(null);
  };

  const isPaymentSettingsConfigured = (eventId) => {
    const settings = paymentSettingsMap?.[eventId];
    return settings && settings.bank?.name && settings.bank?.accountNo && settings.upi;
  };

  if (isLoading) {
    return <LoadingSpinner fullScreen text="Loading events..." />;
  }

  // Handle error state
  if (eventsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Events</h3>
            <p className="text-gray-600 mb-4">
              {eventsError.message || 'Failed to load events. Please try again.'}
            </p>
            <Button onClick={() => window.location.reload()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : false;

  // Simplified view for non-admin users: only event name and a "View Registrations" CTA
  if (!isAdminUser) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-gray-900">Event Management</h1>
          <p className="text-gray-600">
            Select an event below to view all registrations.
          </p>
        </div>

        {events.length > 0 && (
          <SearchBar
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={setSearchTerm}
            mode="SUBMIT"
            placeholder="Search events by name"
            className="max-w-xl"
          />
        )}

        {/* Events List (minimal) */}
        <div className="space-y-4">
          {filteredEvents.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No events available
                </h3>
                <p className="text-gray-600">
                  There are currently no events you can view.
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredEvents.map((event) => (
              <Card key={event.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {event.name}
                    </h3>
                  </div>
                  <Link
                    to={`/admin/events/${eventRef(event)}/registrations`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="w-full sm:w-auto">
                      View Registrations
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Event Management</h1>
            <p className="text-gray-600">Create and manage your events</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Standalone page in public/tools: works without login or internet once opened. */}
            <Button variant="outline" asChild>
              <a href="/tools/offline-badge-printer.html" target="_blank" rel="noopener noreferrer">
                <Printer className="h-4 w-4 mr-2" />
                Offline Badge Printing
              </a>
            </Button>
            <Button onClick={() => setIsCreating(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Event
            </Button>
          </div>
        </div>

        {/* Search and Stats */}
        <div className="flex items-center justify-between bg-white p-4 rounded-lg border">
          <div className="flex-1 max-w-md">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              mode="AUTO"
              placeholder="Search events by name..."
            />
          </div>
          <div className="flex items-center space-x-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{events.length}</p>
              <p className="text-sm text-gray-600">Total Events</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {events.filter(e => e.registered > 0).length}
              </p>
              <p className="text-sm text-gray-600">Active Events</p>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Event Modal */}
      {(isCreating || editingEvent) && (
        <Dialog open={true} onOpenChange={() => {
          if (editingEvent) {
            setEditingEvent(null);
          } else {
            resetForm();
            setIsCreating(false);
          }
        }}>
          <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto p-4 sm:p-6 lg:p-8">
            <DialogHeader>
              <DialogTitle>{editingEvent ? 'Edit Event' : 'Create New Event'}</DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
            {/* Basic Event Info */}
            <div>
              <Label htmlFor="eventName">Event Name *</Label>
              <Input
                id="eventName"
                value={editingEvent ? editingEvent.name : newEvent.name}
                onChange={(e) => editingEvent ? 
                  setEditingEvent(prev => ({ ...prev, name: e.target.value })) :
                  setNewEvent(prev => ({ ...prev, name: e.target.value }))
                }
                placeholder="Enter event name"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="eventType">Event Type</Label>
                <Select 
                  value={editingEvent ? editingEvent.eventType : newEvent.eventType}
                  onValueChange={(value) => editingEvent ?
                    setEditingEvent(prev => ({ ...prev, eventType: value })) :
                    setNewEvent(prev => ({ ...prev, eventType: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="limited">Limited Capacity</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="capacity">Capacity</Label>
                <Input
                  id="capacity"
                  type="number"
                  value={editingEvent ? editingEvent.capacity : newEvent.capacity}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    editingEvent ?
                      setEditingEvent(prev => ({ ...prev, capacity: value })) :
                      setNewEvent(prev => ({ ...prev, capacity: value }));
                  }}
                  placeholder="100"
                />
              </div>
              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={editingEvent ? editingEvent.location : newEvent.location}
                  onChange={(e) => editingEvent ?
                    setEditingEvent(prev => ({ ...prev, location: e.target.value })) :
                    setNewEvent(prev => ({ ...prev, location: e.target.value }))
                  }
                  placeholder="Enter event location"
                />
              </div>
              <div className="space-y-3">
                {/* Face ID toggle */}
                <div className="space-y-2">
                  <Label>Face Recognition</Label>
                  <div className="flex items-center space-x-2">
                    <input
                      id="faceIdEnabled"
                      type="checkbox"
                      className="h-4 w-4"
                      checked={editingEvent ? !!editingEvent.faceIdEnabled : !!newEvent.faceIdEnabled}
                      onChange={(e) => {
                        const value = e.target.checked;
                        if (editingEvent) {
                          setEditingEvent(prev => ({ ...prev, faceIdEnabled: value }));
                        } else {
                          setNewEvent(prev => ({ ...prev, faceIdEnabled: value }));
                        }
                      }}
                    />
                    <Label htmlFor="faceIdEnabled" className="text-sm font-normal">
                      Enable face recognition for this event
                    </Label>
                  </div>
                </div>

                {/* RFID toggle */}
                <div className="space-y-2">
                  <Label>RFID</Label>
                  <div className="flex items-center space-x-2">
                    <input
                      id="rfIdEnabled"
                      type="checkbox"
                      className="h-4 w-4"
                      checked={editingEvent ? !!editingEvent.rfIdEnabled : !!newEvent.rfIdEnabled}
                      onChange={(e) => {
                        const value = e.target.checked;
                        if (editingEvent) {
                          setEditingEvent(prev => ({ ...prev, rfIdEnabled: value }));
                        } else {
                          setNewEvent(prev => ({ ...prev, rfIdEnabled: value }));
                        }
                      }}
                    />
                    <Label htmlFor="rfIdEnabled" className="text-sm font-normal">
                      Enable RFID-based check-in for this event
                    </Label>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="eventStartDate">Event Start Date & Time *</Label>
                <Input
                  id="eventStartDate"
                  type="datetime-local"
                  value={editingEvent ? editingEvent.eventDate : newEvent.eventDate}
                  onChange={(e) => editingEvent ?
                    setEditingEvent(prev => ({ ...prev, eventDate: e.target.value })) :
                    setNewEvent(prev => ({ ...prev, eventDate: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="eventEndDate">Event End Date & Time *</Label>
                <Input
                  id="eventEndDate"
                  type="datetime-local"
                  value={editingEvent ? editingEvent.eventEndDate : newEvent.eventEndDate}
                  onChange={(e) => editingEvent ?
                    setEditingEvent(prev => ({ ...prev, eventEndDate: e.target.value })) :
                    setNewEvent(prev => ({ ...prev, eventEndDate: e.target.value }))
                  }
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <div className="mt-1">
                <ReactQuill
                  theme="snow"
                  value={editingEvent ? (editingEvent.description || '') : (newEvent.description || '')}
                  onChange={(value) =>
                    editingEvent
                      ? setEditingEvent(prev => ({ ...prev, description: value }))
                      : setNewEvent(prev => ({ ...prev, description: value }))
                  }
                  placeholder="Enter event description"
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, false] }],
                      ['bold', 'italic', 'underline'],
                      [{ list: 'ordered' }, { list: 'bullet' }],
                      ['link'],
                      ['clean'],
                    ],
                  }}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="metaData">Event metadata (shown below event name on registration page)</Label>
              <div className="mt-1">
                <ReactQuill
                  theme="snow"
                  value={editingEvent ? (editingEvent.metaData ?? '') : (newEvent.metaData ?? '')}
                  onChange={(value) =>
                    editingEvent
                      ? setEditingEvent(prev => ({ ...prev, metaData: value }))
                      : setNewEvent(prev => ({ ...prev, metaData: value }))
                  }
                  placeholder="e.g. Organised by Department of Lab Sciences, Regency Hospital, Kanpur"
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, false] }],
                      ['bold', 'italic', 'underline'],
                      [{ list: 'ordered' }, { list: 'bullet' }],
                      ['link'],
                      ['clean'],
                    ],
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="capacity">Capacity</Label>
                <Input
                  id="capacity"
                  type="number"
                  value={editingEvent ? editingEvent.capacity : newEvent.capacity}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    editingEvent ?
                      setEditingEvent(prev => ({ ...prev, capacity: value })) :
                      setNewEvent(prev => ({ ...prev, capacity: value }));
                  }}
                />
              </div>
              <div>
                <Label htmlFor="price">Price</Label>
                <div className="flex gap-2">
                  <Select 
                    value={editingEvent ? editingEvent.currency : newEvent.currency}
                    onValueChange={(value) => editingEvent ?
                      setEditingEvent(prev => ({ ...prev, currency: value })) :
                      setNewEvent(prev => ({ ...prev, currency: value }))
                    }
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">INR (₹)</SelectItem>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="GBP">GBP (£)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={editingEvent ? editingEvent.price : newEvent.price}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      editingEvent ?
                        setEditingEvent(prev => ({ ...prev, price: value })) :
                        setNewEvent(prev => ({ ...prev, price: value }));
                    }}
                    disabled={(editingEvent ? editingEvent.eventType : newEvent.eventType) === 'free'}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <Label>Badge Configuration</Label>
                <div className="text-sm text-gray-600 p-3 bg-blue-50 rounded-lg">
                  <p>Badge design is now configured separately for each event.</p>
                  <p>{editingEvent ? 'Go to the event\'s badge configuration page to modify the design.' : 'After creating the event, go to the event\'s badge configuration page to set up the design.'}</p>
                </div>
              </div>
            </div>

            {/* Editing keeps only basics and the form here; everything else lives on the event settings page. */}
            {editingEvent ? (
              <div className="flex flex-col gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-medium text-blue-900">Looking for more options?</p>
                  <p className="text-sm text-blue-800">Notifications, check-in, payments, invoices, desk, website, team access, sign-in page and custom domain are on the event settings page.</p>
                </div>
                <Button type="button" variant="outline" className="shrink-0" onClick={() => navigate(`/admin/events/${eventRef(editingEvent)}/settings`)}>
                  <Settings className="h-4 w-4 mr-2" />
                  Open event settings
                </Button>
              </div>
            ) : (
            <>
            {/* Notification Channels */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Send Email Notifications</Label>
                <div className="flex items-center space-x-2">
                  <input
                    id="sendEmail"
                    type="checkbox"
                    className="h-4 w-4"
                    checked={editingEvent ? editingEvent.sendEmail !== false : newEvent.sendEmail !== false}
                    onChange={(e) => {
                      const value = e.target.checked;
                      if (editingEvent) {
                        setEditingEvent(prev => ({ ...prev, sendEmail: value }));
                      } else {
                        setNewEvent(prev => ({ ...prev, sendEmail: value }));
                      }
                    }}
                  />
                  <Label htmlFor="sendEmail" className="text-sm font-normal">
                    Enable email notifications for this event
                  </Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Send WhatsApp Notifications</Label>
                <div className="flex items-center space-x-2">
                  <input
                    id="sendWhatsapp"
                    type="checkbox"
                    className="h-4 w-4"
                    checked={editingEvent ? editingEvent.sendWhatsapp !== false : newEvent.sendWhatsapp !== false}
                    onChange={(e) => {
                      const value = e.target.checked;
                      if (editingEvent) {
                        setEditingEvent(prev => ({ ...prev, sendWhatsapp: value }));
                      } else {
                        setNewEvent(prev => ({ ...prev, sendWhatsapp: value }));
                      }
                    }}
                  />
                  <Label htmlFor="sendWhatsapp" className="text-sm font-normal">
                    Enable WhatsApp notifications for this event
                  </Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Confirmation Email Button</Label>
                <div className="flex items-center space-x-2">
                  <input
                    id="showConfirmationEmailButton"
                    type="checkbox"
                    className="h-4 w-4"
                    checked={editingEvent ? editingEvent.showConfirmationEmailButton !== false : newEvent.showConfirmationEmailButton !== false}
                    onChange={(e) => {
                      const value = e.target.checked;
                      if (editingEvent) {
                        setEditingEvent(prev => ({ ...prev, showConfirmationEmailButton: value }));
                      } else {
                        setNewEvent(prev => ({ ...prev, showConfirmationEmailButton: value }));
                      }
                    }}
                  />
                  <Label htmlFor="showConfirmationEmailButton" className="text-sm font-normal">
                    Show "Send Confirmation Email" button on registrations
                  </Label>
                </div>
              </div>
            </div>

            {/* Feature Configuration */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Offline Support</Label>
                <div className="flex items-center space-x-2">
                  <input
                    id="offlineEnabled"
                    type="checkbox"
                    className="h-4 w-4"
                    checked={editingEvent ? !!editingEvent.offlineEnabled : !!newEvent.offlineEnabled}
                    onChange={(e) => {
                      const value = e.target.checked;
                      if (editingEvent) {
                        setEditingEvent(prev => ({ ...prev, offlineEnabled: value }));
                      } else {
                        setNewEvent(prev => ({ ...prev, offlineEnabled: value }));
                      }
                    }}
                  />
                  <Label htmlFor="offlineEnabled" className="text-sm font-normal">
                    Allow this event to work offline (cache & sync)
                  </Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Lockers</Label>
                <div className="flex items-center space-x-2">
                  <input
                    id="lockerEnabled"
                    type="checkbox"
                    className="h-4 w-4"
                    checked={editingEvent ? !!editingEvent.lockerEnabled : !!newEvent.lockerEnabled}
                    onChange={(e) => {
                      const value = e.target.checked;
                      if (editingEvent) {
                        setEditingEvent(prev => ({ ...prev, lockerEnabled: value }));
                      } else {
                        setNewEvent(prev => ({ ...prev, lockerEnabled: value }));
                      }
                    }}
                  />
                  <Label htmlFor="lockerEnabled" className="text-sm font-normal">
                    Enable locker assignment for this event
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    id="enableCouponCode"
                    type="checkbox"
                    className="h-4 w-4"
                    checked={editingEvent ? !!editingEvent.enableCouponCode : !!newEvent.enableCouponCode}
                    onChange={(e) => {
                      const value = e.target.checked;
                      if (editingEvent) {
                        setEditingEvent(prev => ({ ...prev, enableCouponCode: value }));
                      } else {
                        setNewEvent(prev => ({ ...prev, enableCouponCode: value }));
                      }
                    }}
                  />
                  <Label htmlFor="enableCouponCode" className="text-sm font-normal">
                    Enable coupon codes for this event
                  </Label>
                </div>

                {(editingEvent ? !!editingEvent.lockerEnabled : !!newEvent.lockerEnabled) && (
                  <div className="pl-6 space-y-2 border-l-2 border-gray-100">
                    <div className="flex items-center space-x-2">
                      <input
                        id="allowSharedLockers"
                        type="checkbox"
                        className="h-4 w-4"
                        checked={editingEvent ? !!editingEvent.allowSharedLockers : !!newEvent.allowSharedLockers}
                        onChange={(e) => {
                          const value = e.target.checked;
                          if (editingEvent) {
                            setEditingEvent(prev => ({ ...prev, allowSharedLockers: value }));
                          } else {
                            setNewEvent(prev => ({ ...prev, allowSharedLockers: value }));
                          }
                        }}
                      />
                      <Label htmlFor="allowSharedLockers" className="text-sm font-normal">
                        Allow the same locker to be shared by multiple users
                      </Label>
                    </div>

                    {(editingEvent ? !!editingEvent.allowSharedLockers : !!newEvent.allowSharedLockers) && (
                      <div className="flex items-center space-x-2">
                        <Label htmlFor="maxUsersPerLocker" className="text-sm font-normal text-gray-600">
                          Max users per locker
                        </Label>
                        <Input
                          id="maxUsersPerLocker"
                          type="number"
                          min={2}
                          max={100}
                          className="w-24"
                          value={editingEvent ? (editingEvent.maxUsersPerLocker ?? 4) : (newEvent.maxUsersPerLocker ?? 4)}
                          onChange={(e) => {
                            const value = Math.max(1, parseInt(e.target.value, 10) || 1);
                            if (editingEvent) {
                              setEditingEvent(prev => ({ ...prev, maxUsersPerLocker: value }));
                            } else {
                              setNewEvent(prev => ({ ...prev, maxUsersPerLocker: value }));
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Scan Page Inputs</Label>
                {[
                  { key: 'scanManualSearchEnabled', label: 'Show "Search attendee" box' },
                  { key: 'scanQrInputEnabled', label: 'Show QR code / Registration ID scanner' },
                  { key: 'scanShowLogo', label: 'Show event logo on scan page', defaultOn: false },
                  { key: 'scanShowStats', label: 'Show scan statistics on scan page' },
                ].map(({ key, label, defaultOn = true }) => (
                  <div key={key} className="flex items-center space-x-2">
                    <input
                      id={key}
                      type="checkbox"
                      className="h-4 w-4"
                      checked={defaultOn ? (editingEvent || newEvent)[key] !== false : !!(editingEvent || newEvent)[key]}
                      onChange={(e) => {
                        const value = e.target.checked;
                        if (editingEvent) {
                          setEditingEvent(prev => ({ ...prev, [key]: value }));
                        } else {
                          setNewEvent(prev => ({ ...prev, [key]: value }));
                        }
                      }}
                    />
                    <Label htmlFor={key} className="text-sm font-normal">{label}</Label>
                  </div>
                ))}
                {(editingEvent || newEvent).scanShowLogo && (() => {
                  const target = editingEvent || newEvent;
                  const setScanLogoUrl = (url) => {
                    const update = prev => ({ ...prev, scanLogoUrl: url });
                    if (editingEvent) setEditingEvent(update); else setNewEvent(update);
                  };
                  const fallback = target.formLogoUrl || target.eventLogo;
                  const preview = target.scanLogoUrl || fallback;
                  return (
                    <div className="pl-6 pt-1 space-y-2">
                      <p className="text-xs text-gray-500">
                        Optional scan-page logo. Leave empty to use the registration form logo; it never changes the registration page.
                      </p>
                      {preview && (
                        <img src={preview} alt="Scan page logo" className="max-h-16 object-contain rounded border" />
                      )}
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                          className="text-sm"
                          disabled={uploadingScanLogo}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = '';
                            if (!file) return;
                            setUploadingScanLogo(true);
                            try {
                              setScanLogoUrl(await ApiService.uploadFormSchemaAsset('scan_logo', await resizeImage(file, IMAGE_LIMITS.scan_logo)));
                            } catch (err) {
                              showError(err.message || 'Logo upload failed');
                            } finally {
                              setUploadingScanLogo(false);
                            }
                          }}
                        />
                        {target.scanLogoUrl && (
                          <Button type="button" variant="outline" size="sm" onClick={() => setScanLogoUrl('')}>
                            Use form logo
                          </Button>
                        )}
                      </div>
                      {!target.scanLogoUrl && (
                        <p className="text-xs text-gray-500">{fallback ? 'Using the form logo.' : 'No form logo set; nothing will show until you upload one.'}</p>
                      )}
                    </div>
                  );
                })()}
                {(() => {
                  const target = editingEvent || newEvent;
                  const labels = (target.formSchema || []).map(f => f.label).filter(Boolean);
                  if (labels.length === 0) return null;
                  const normalize = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                  const selected = (target.publicSearchFields ?? DEFAULT_PUBLIC_SEARCH_FIELDS).map(normalize);
                  const toggle = (fieldLabel, checked) => {
                    const current = labels.filter(l => selected.includes(normalize(l)));
                    const next = checked ? [...current, fieldLabel] : current.filter(l => l !== fieldLabel);
                    const update = prev => ({ ...prev, publicSearchFields: next });
                    if (editingEvent) setEditingEvent(update); else setNewEvent(update);
                  };
                  return (
                    <div className="pl-6 pt-1 space-y-1">
                      <p className="text-xs text-gray-500">Fields shown to scanners who are not logged in (none = no public search)</p>
                      {labels.map(fieldLabel => (
                        <div key={fieldLabel} className="flex items-center space-x-2">
                          <input
                            id={`public-${fieldLabel}`}
                            type="checkbox"
                            className="h-4 w-4"
                            checked={selected.includes(normalize(fieldLabel))}
                            onChange={(e) => toggle(fieldLabel, e.target.checked)}
                          />
                          <Label htmlFor={`public-${fieldLabel}`} className="text-sm font-normal">{fieldLabel}</Label>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>

            {(() => {
              const target = editingEvent || newEvent;
              const setField = (key, value) => {
                if (editingEvent) setEditingEvent(prev => ({ ...prev, [key]: value }));
                else setNewEvent(prev => ({ ...prev, [key]: value }));
              };
              const textareaClass = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm';
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Online Registrations</Label>
                    <div className="flex items-center space-x-2">
                      <input
                        id="registrationsClosed"
                        type="checkbox"
                        className="h-4 w-4"
                        checked={!!target.registrationsClosed}
                        onChange={(e) => setField('registrationsClosed', e.target.checked)}
                      />
                      <Label htmlFor="registrationsClosed" className="text-sm font-normal">Close registrations</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        id="showFormLogo"
                        type="checkbox"
                        className="h-4 w-4"
                        checked={target.showFormLogo !== false}
                        onChange={(e) => setField('showFormLogo', e.target.checked)}
                      />
                      <Label htmlFor="showFormLogo" className="text-sm font-normal">Show logo on registration form</Label>
                    </div>
                    <p className="text-xs text-gray-500">Logo banner: 1600 × 400 px (4:1), PNG/WebP under 500 KB. Shown up to 896 px wide and 240 px tall.</p>
                    <textarea
                      className={textareaClass}
                      rows={2}
                      placeholder="Message when closed or full (default: event has reached its capacity…)"
                      value={target.registrationClosedMessage || ''}
                      onChange={(e) => setField('registrationClosedMessage', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>On-spot Registrations</Label>
                    <div className="flex items-center space-x-2">
                      <input
                        id="onSpotRegistrationsClosed"
                        type="checkbox"
                        className="h-4 w-4"
                        checked={!!target.onSpotRegistrationsClosed}
                        onChange={(e) => setField('onSpotRegistrationsClosed', e.target.checked)}
                      />
                      <Label htmlFor="onSpotRegistrationsClosed" className="text-sm font-normal">Close on-spot registrations</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Label htmlFor="onSpotCapacity" className="text-sm font-normal text-gray-600">On-spot capacity</Label>
                      <Input
                        id="onSpotCapacity"
                        type="number"
                        min={0}
                        className="w-28"
                        placeholder="Unlimited"
                        value={target.onSpotCapacity || ''}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10);
                          // 0 clears the limit on save; the backend treats <= 0 as unlimited.
                          setField('onSpotCapacity', Number.isFinite(value) && value > 0 ? value : 0);
                        }}
                      />
                    </div>
                    <textarea
                      className={textareaClass}
                      rows={2}
                      placeholder="Message when on-spot is closed or full"
                      value={target.onSpotClosedMessage || ''}
                      onChange={(e) => setField('onSpotClosedMessage', e.target.value)}
                    />
                  </div>
                </div>
              );
            })()}

            {['paid', 'PAID'].includes((editingEvent || newEvent).eventType) && (
              <div className="space-y-2">
                <Label>Payment Approval</Label>
                {[
                  { key: 'autoApproveOnline', label: 'Auto-approve online registrations' },
                  { key: 'autoApproveOnSpot', label: 'Auto-approve on-spot registrations' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center space-x-2">
                    <input
                      id={key}
                      type="checkbox"
                      className="h-4 w-4"
                      checked={!!(editingEvent || newEvent)[key]}
                      onChange={(e) => {
                        const value = e.target.checked;
                        if (editingEvent) setEditingEvent(prev => ({ ...prev, [key]: value }));
                        else setNewEvent(prev => ({ ...prev, [key]: value }));
                      }}
                    />
                    <Label htmlFor={key} className="text-sm font-normal">{label}</Label>
                  </div>
                ))}
                <p className="text-xs text-gray-500">
                  Registrations that submit payment details are confirmed immediately and get the confirmation email. Unticked channels still need manual approval.
                </p>
              </div>
            )}

            {(() => {
              const target = editingEvent || newEvent;
              const settings = target.invoiceSettings || {};
              const setInvoice = (key, value) => {
                const update = prev => ({ ...prev, invoiceSettings: { ...(prev.invoiceSettings || {}), [key]: value } });
                if (editingEvent) setEditingEvent(update); else setNewEvent(update);
              };
              const fields = [
                ['sellerName', 'Legal name (supplier)'],
                ['gstin', 'GSTIN'],
                ['pan', 'PAN'],
                ['address', 'Address'],
                ['state', 'State'],
                ['stateCode', 'State code'],
                ['placeOfSupply', 'Place of supply (event state, blank = supplier state)'],
                ['sacCode', 'SAC code'],
                ['invoicePrefix', 'Invoice number prefix (e.g. UPP/26-27/)'],
                ['email', 'Billing email'],
                ['phone', 'Billing phone'],
                ['buyerGstinField', 'Form field with attendee GSTIN (optional)'],
                ['authorisedSignatory', 'Authorised signatory'],
              ];
              return (
                <div className="space-y-2">
                  <Label>Payment Invoice (GST)</Label>
                  <div className="flex items-center space-x-2">
                    <input
                      id="invoiceEnabled"
                      type="checkbox"
                      className="h-4 w-4"
                      checked={!!settings.enabled}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const update = prev => ({
                          ...prev,
                          invoiceSettings: {
                            ...(prev.invoiceSettings || {}),
                            enabled: checked,
                            gstRate: prev.invoiceSettings?.gstRate ?? 18,
                          },
                        });
                        if (editingEvent) setEditingEvent(update); else setNewEvent(update);
                      }}
                    />
                    <Label htmlFor="invoiceEnabled" className="text-sm font-normal">
                      Send a GST tax invoice instead of the plain receipt (amounts are treated as GST-inclusive)
                    </Label>
                  </div>
                  {settings.enabled && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-6">
                      {fields.map(([key, label]) => (
                        <div key={key} className="space-y-1">
                          <Label htmlFor={`invoice-${key}`} className="text-xs text-gray-600">{label}</Label>
                          <Input
                            id={`invoice-${key}`}
                            value={settings[key] || ''}
                            onChange={(e) => setInvoice(key, e.target.value)}
                          />
                        </div>
                      ))}
                      <div className="space-y-1">
                        <Label htmlFor="invoice-gstRate" className="text-xs text-gray-600">GST rate % (included in price)</Label>
                        <Input
                          id="invoice-gstRate"
                          type="number"
                          min={0}
                          step="0.01"
                          value={settings.gstRate ?? 18}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            setInvoice('gstRate', Number.isFinite(value) && value >= 0 ? value : 0);
                          }}
                        />
                      </div>
                      <div className="space-y-1 md:col-span-2">
                        <Label htmlFor="invoice-footerNote" className="text-xs text-gray-600">Footer note / terms</Label>
                        <textarea
                          id="invoice-footerNote"
                          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          rows={2}
                          value={settings.footerNote || ''}
                          onChange={(e) => setInvoice('footerNote', e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <PageBackgroundsEditor
              value={(editingEvent || newEvent).pageBackgrounds}
              onChange={(next) => {
                // Always send an object so clearing every page also clears it on the server.
                const update = prev => ({ ...prev, pageBackgrounds: next });
                if (editingEvent) setEditingEvent(update); else setNewEvent(update);
              }}
            />

            <div className="space-y-2">
              <Label htmlFor="onSpotAccess">On-spot Registration Access</Label>
              <select
                id="onSpotAccess"
                className="w-full md:w-96 h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={(editingEvent || newEvent).onSpotAccess || 'LOGIN'}
                onChange={(e) => {
                  const value = e.target.value;
                  if (editingEvent) setEditingEvent(prev => ({ ...prev, onSpotAccess: value }));
                  else setNewEvent(prev => ({ ...prev, onSpotAccess: value }));
                }}
              >
                <option value="LOGIN">Logged-in staff only</option>
                <option value="PUBLIC">Anyone (logged in or not)</option>
                <option value="DISABLED">Disabled</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label>Event Desk</Label>
              <div className="flex items-center space-x-2">
                <input
                  id="deskPublic"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={!!(editingEvent || newEvent).deskPublic}
                  onChange={(e) => {
                    const value = e.target.checked;
                    if (editingEvent) setEditingEvent(prev => ({ ...prev, deskPublic: value }));
                    else setNewEvent(prev => ({ ...prev, deskPublic: value }));
                  }}
                />
                <Label htmlFor="deskPublic" className="text-sm font-normal">
                  Open the event desk to everyone (kiosk mode, no login)
                </Label>
              </div>
              {(editingEvent || newEvent).id ? (
                <div className="flex items-center gap-2">
                  <code className="flex-1 truncate rounded bg-gray-100 px-2 py-1 text-xs">
                    {`${window.location.origin}/desk/${(editingEvent || newEvent).id}`}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/desk/${(editingEvent || newEvent).id}`)}
                  >
                    Copy
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-gray-500">The desk link appears here once the event is saved.</p>
              )}
              <p className="text-xs text-gray-500">
                Without login the desk shows On-spot registration only when access above is "Anyone".
              </p>
            </div>

            <div className="space-y-2">
              <Label>Badge Printing</Label>
              <div className="flex items-center space-x-2">
                <input
                  id="badgeShareIos"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={!!(editingEvent || newEvent).badgeShareIos}
                  onChange={(e) => {
                    const value = e.target.checked;
                    if (editingEvent) setEditingEvent(prev => ({ ...prev, badgeShareIos: value }));
                    else setNewEvent(prev => ({ ...prev, badgeShareIos: value }));
                  }}
                />
                <Label htmlFor="badgeShareIos" className="text-sm font-normal">
                  Show "Share badge" on iPad/iPhone (exact badge size via AirPrint)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  id="badgeShareAndroid"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={!!(editingEvent || newEvent).badgeShareAndroid}
                  onChange={(e) => {
                    const value = e.target.checked;
                    if (editingEvent) setEditingEvent(prev => ({ ...prev, badgeShareAndroid: value }));
                    else setNewEvent(prev => ({ ...prev, badgeShareAndroid: value }));
                  }}
                />
                <Label htmlFor="badgeShareAndroid" className="text-sm font-normal">
                  Show "Share badge" on Android (send the badge PDF to a print app)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  id="offlineBadgeImport"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={!!(editingEvent || newEvent).offlineBadgeImport}
                  onChange={(e) => {
                    const value = e.target.checked;
                    if (editingEvent) setEditingEvent(prev => ({ ...prev, offlineBadgeImport: value }));
                    else setNewEvent(prev => ({ ...prev, offlineBadgeImport: value }));
                  }}
                />
                <Label htmlFor="offlineBadgeImport" className="text-sm font-normal">
                  Allow importing attendees into the Offline Badge Printer
                </Label>
              </div>
              {(editingEvent || newEvent).offlineBadgeImport && (
                <p className="text-xs text-amber-700 pl-6">
                  No sign-in needed: anyone who opens the badge printer page can import this event's attendee names and form answers.
                </p>
              )}
              <div className="flex items-center space-x-2">
                <input
                  id="showOnWebsite"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={!!(editingEvent || newEvent).showOnWebsite}
                  onChange={(e) => {
                    const value = e.target.checked;
                    if (editingEvent) setEditingEvent(prev => ({ ...prev, showOnWebsite: value }));
                    else setNewEvent(prev => ({ ...prev, showOnWebsite: value }));
                  }}
                />
                <Label htmlFor="showOnWebsite" className="text-sm font-normal">
                  Show on website (name, dates, venue, logo and registration count in “Recent events”)
                </Label>
              </div>
              {(editingEvent || newEvent).showOnWebsite && (() => {
                const info = (editingEvent || newEvent).websiteInfo || {};
                const updateWebsite = (patch) => {
                  const setter = editingEvent ? setEditingEvent : setNewEvent;
                  setter(prev => ({ ...prev, websiteInfo: { ...(prev.websiteInfo || {}), ...patch } }));
                };
                const links = info.links || [];
                return (
                  <div className="pl-6 space-y-3 border-l-2 border-blue-100 ml-2">
                    <div className="flex items-center space-x-2">
                      <input
                        id="websiteAllowRegistration"
                        type="checkbox"
                        className="h-4 w-4"
                        checked={!!info.allowRegistration}
                        onChange={(e) => updateWebsite({ allowRegistration: e.target.checked })}
                      />
                      <Label htmlFor="websiteAllowRegistration" className="text-sm font-normal">
                        Allow registration from our public events page
                      </Label>
                    </div>
                    <div>
                      <Label htmlFor="websiteAbout" className="text-sm">About the event (shown on the events page)</Label>
                      <Textarea id="websiteAbout" rows={3} maxLength={3000} value={info.about || ''}
                        onChange={(e) => updateWebsite({ about: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="websiteOfficial" className="text-sm">Official website</Label>
                        <Input id="websiteOfficial" placeholder="https://" value={info.officialUrl || ''}
                          onChange={(e) => updateWebsite({ officialUrl: e.target.value })} />
                      </div>
                      <div>
                        <Label htmlFor="websiteOrganiser" className="text-sm">Organising committee link</Label>
                        <Input id="websiteOrganiser" placeholder="https://" value={info.organiserUrl || ''}
                          onChange={(e) => updateWebsite({ organiserUrl: e.target.value })} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">More links (brochure, venue map, …)</Label>
                      {links.map((link, index) => (
                        <div key={index} className="flex gap-2">
                          <Input className="w-40" placeholder="Label" value={link.label || ''}
                            onChange={(e) => updateWebsite({ links: links.map((l, i) => (i === index ? { ...l, label: e.target.value } : l)) })} />
                          <Input placeholder="https://" value={link.url || ''}
                            onChange={(e) => updateWebsite({ links: links.map((l, i) => (i === index ? { ...l, url: e.target.value } : l)) })} />
                          <Button type="button" variant="ghost" size="sm"
                            onClick={() => updateWebsite({ links: links.filter((_, i) => i !== index) })}>Remove</Button>
                        </div>
                      ))}
                      {links.length < 10 && (
                        <Button type="button" variant="outline" size="sm"
                          onClick={() => updateWebsite({ links: [...links, { label: '', url: '' }] })}>Add link</Button>
                      )}
                    </div>
                  </div>
                );
              })()}
              <div className="flex items-center space-x-2">
                <input
                  id="selfServiceEnabled"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={!!(editingEvent || newEvent).selfServiceEnabled}
                  onChange={(e) => {
                    const value = e.target.checked;
                    if (editingEvent) setEditingEvent(prev => ({ ...prev, selfServiceEnabled: value }));
                    else setNewEvent(prev => ({ ...prev, selfServiceEnabled: value }));
                  }}
                />
                <Label htmlFor="selfServiceEnabled" className="text-sm font-normal">
                  Let attendees view, edit and download their registration ("My registration" link in the email)
                </Label>
              </div>
              {(editingEvent || newEvent).selfServiceEnabled && (
                <div className="pl-6 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Label htmlFor="selfServiceEditUntil" className="text-sm font-normal">Changes allowed until (IST)</Label>
                    <Input
                      id="selfServiceEditUntil"
                      type="datetime-local"
                      className="w-auto"
                      value={toIstInputValue((editingEvent || newEvent).selfServiceEditUntil)}
                      onChange={(e) => {
                        const value = istInputToBackend(e.target.value);
                        if (editingEvent) setEditingEvent(prev => ({ ...prev, selfServiceEditUntil: value }));
                        else setNewEvent(prev => ({ ...prev, selfServiceEditUntil: value }));
                      }}
                    />
                    <span className="text-xs text-gray-500">Leave empty to keep changes open.</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      id="selfServiceAllowCancel"
                      type="checkbox"
                      className="h-4 w-4"
                      checked={!!(editingEvent || newEvent).selfServiceAllowCancel}
                      onChange={(e) => {
                        const value = e.target.checked;
                        if (editingEvent) setEditingEvent(prev => ({ ...prev, selfServiceAllowCancel: value }));
                        else setNewEvent(prev => ({ ...prev, selfServiceAllowCancel: value }));
                      }}
                    />
                    <Label htmlFor="selfServiceAllowCancel" className="text-sm font-normal">
                      Attendees can cancel their own registration
                    </Label>
                  </div>
                  <p className="text-xs text-gray-500">
                    Changes that cost more stay pending until you approve the extra payment in the registration details.
                  </p>
                </div>
              )}
              <div className="flex items-center space-x-2">
                <input
                  id="showPrintProgress"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={(editingEvent || newEvent).showPrintProgress !== false}
                  onChange={(e) => {
                    const value = e.target.checked;
                    if (editingEvent) setEditingEvent(prev => ({ ...prev, showPrintProgress: value }));
                    else setNewEvent(prev => ({ ...prev, showPrintProgress: value }));
                  }}
                />
                <Label htmlFor="showPrintProgress" className="text-sm font-normal">
                  Desk: show "Badge printing in progress" before returning to the desk
                </Label>
              </div>
            </div>

            {editingEvent?.id && <LanSyncKeyPanel eventId={editingEvent.id} />}

            {editingEvent && (
              <div className="space-y-2">
                <Label>On-spot Registration Form</Label>
                <p className="text-sm text-gray-600">Use a different (e.g. shorter) form for on-spot registrations.</p>
                <select
                  className="w-full md:w-96 h-10 rounded-md border border-input bg-background px-3 text-sm"
                  value={editingEvent.onSpotSchemaId || ''}
                  onChange={(e) => {
                    const schema = formSchemas.find(s => s.schemaId === e.target.value);
                    setEditingEvent(prev => ({
                      ...prev,
                      onSpotSchemaId: schema ? schema.schemaId : '',
                      // Copied like the main form so later schema edits don't silently change this event.
                      onSpotFormSchema: schema ? JSON.parse(JSON.stringify(schema.fields || [])) : [],
                    }));
                  }}
                >
                  <option value="">Same as registration form</option>
                  {formSchemas.map(schema => (
                    <option key={schema.schemaId} value={schema.schemaId}>{schema.name}</option>
                  ))}
                </select>
              </div>
            )}

            </>
            )}

            {/* Form Schema Selection */}
            <div>
              <Label className="text-lg font-medium">Registration Form Schema</Label>
              <p className="text-sm text-gray-600 mt-1 mb-4">
                Choose a pre-built form schema or create a new one
              </p>
              <SchemaSelector
                schemas={formSchemas}
                selectedSchemaId={editingEvent ? editingEvent.selectedSchemaId : newEvent.selectedSchemaId}
                onSelect={(schemaId) => {
                  const selectedSchema = formSchemas.find(schema => schema.schemaId === schemaId);
                  if (selectedSchema) {
                    console.log('🔄 Schema onSelect triggered with schemaId:', schemaId);
                    
                    const hasPaidOptions = selectedSchema.fields?.some(field => 
                      field.optionsWithPricing?.some(option => option.price > 0)
                    );
                    
                    const minPrice = hasPaidOptions ? Math.min(
                      ...selectedSchema.fields
                        .filter(field => field.optionsWithPricing)
                        .flatMap(field => field.optionsWithPricing.map(option => option.price))
                        .filter(price => price > 0)
                    ) : 0;

                    if (editingEvent) {
                      // Only update formSchema if user is actually changing to a different schema
                      // This prevents overwriting the event's custom formSchema with blacklist data
                      const isSchemaChanging = editingEvent.selectedSchemaId !== schemaId;
                      
                      // Check if current formSchema has blacklist data (non-empty blacklist arrays)
                      const hasBlacklistData = editingEvent.formSchema?.some(field => {
                        // Check field-level blacklists
                        if (field.blackedListedQrPurposes && field.blackedListedQrPurposes.length > 0) {
                          return true;
                        }
                        // Check option-level blacklists
                        if (field.optionWithBlackLists && field.optionWithBlackLists.length > 0) {
                          return field.optionWithBlackLists.some(opt => 
                            opt.blackListedQrPurposes && opt.blackListedQrPurposes.length > 0
                          );
                        }
                        return false;
                      });
                      
                  
                      
                      if (isSchemaChanging) {
                        if (hasBlacklistData) {
                          console.log('  ⚠️ Schema changing but has blacklist data - user confirmation needed');
                          // TODO: Could add a confirmation dialog here
                          // For now, preserve the data
                          setEditingEvent(prev => ({
                            ...prev,
                            selectedSchemaId: schemaId
                          }));
                        } else {
                          console.log('  ✅ Updating formSchema from selected schema');
                          // Deep clone the schema fields to prevent reference sharing
                          const clonedFields = (selectedSchema.fields || []).map(field => {
                            const clonedField = { 
                              ...field,
                              blackedListedQrPurposes: field.blackedListedQrPurposes ? [...field.blackedListedQrPurposes] : []
                            };
                            
                            // Deep clone dropdown/radio options
                            if (field.type === 'dropdown' || field.type === 'radio') {
                              if (field.options) clonedField.options = [...field.options];
                              if (field.optionsWithPricing) {
                                clonedField.optionsWithPricing = field.optionsWithPricing.map(opt => ({
                                  ...opt,
                                  blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : []
                                }));
                              }
                              if (field.optionWithBlackLists) {
                                clonedField.optionWithBlackLists = field.optionWithBlackLists.map(opt => ({
                                  ...opt,
                                  blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : []
                                }));
                              }
                            }
                            
                            return clonedField;
                          });
                          
                          setEditingEvent(prev => ({
                            ...prev,
                            selectedSchemaId: schemaId,
                            formSchema: clonedFields,
                            passes: JSON.parse(JSON.stringify(selectedSchema.passes || [])),
                            groups: JSON.parse(JSON.stringify(selectedSchema.groups || [])),
                            passesLoaded: true,
                            formLogoUrl: selectedSchema.formLogoUrl,
                            formBackgroundUrl: selectedSchema.formBackgroundUrl,
                            eventType: hasPaidOptions ? 'paid' : prev.eventType,
                            price: hasPaidOptions ? minPrice : prev.price
                          }));
                        }
                      } else {
                        console.log('  ⏭️ Schema not changing, preserving existing formSchema');
                        // Just update selectedSchemaId without touching formSchema
                        setEditingEvent(prev => ({
                          ...prev,
                          selectedSchemaId: schemaId
                        }));
                      }
                    } else {
                      // Deep clone the schema fields to prevent reference sharing
                      const clonedFields = (selectedSchema.fields || []).map(field => {
                        const clonedField = { 
                          ...field,
                          blackedListedQrPurposes: field.blackedListedQrPurposes ? [...field.blackedListedQrPurposes] : []
                        };
                        
                        // Deep clone dropdown/radio options
                        if (field.type === 'dropdown' || field.type === 'radio') {
                          if (field.options) clonedField.options = [...field.options];
                          if (field.optionsWithPricing) {
                            clonedField.optionsWithPricing = field.optionsWithPricing.map(opt => ({
                              ...opt,
                              blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : []
                            }));
                          }
                          if (field.optionWithBlackLists) {
                            clonedField.optionWithBlackLists = field.optionWithBlackLists.map(opt => ({
                              ...opt,
                              blackListedQrPurposes: opt.blackListedQrPurposes ? [...opt.blackListedQrPurposes] : []
                            }));
                          }
                        }
                        
                        return clonedField;
                      });
                      
                      setNewEvent(prev => ({
                        ...prev,
                        selectedSchemaId: schemaId,
                        formSchema: clonedFields,
                        passes: JSON.parse(JSON.stringify(selectedSchema.passes || [])),
                        groups: JSON.parse(JSON.stringify(selectedSchema.groups || [])),
                        passesLoaded: true,
                        formLogoUrl: selectedSchema.formLogoUrl,
                        formBackgroundUrl: selectedSchema.formBackgroundUrl,
                        eventType: hasPaidOptions ? 'paid' : prev.eventType,
                        price: hasPaidOptions ? minPrice : prev.price
                      }));
                    }
                    
                    if (hasPaidOptions) {
                      showSuccess(`Event automatically set to paid (min price: ₹${minPrice}) due to pricing in form schema`);
                    }
                  }
                }}
                onCreateNew={handleCreateNewSchema}
                className="mt-4"
              />
              
              {/* Form Builder for Editing Event (with blacklist configuration) */}
              {editingEvent && editingEvent.formSchema?.length > 0 && (
                <div className="mt-6">
                  <div className="border-t pt-6">
                    <Label className="text-md font-medium mb-2 block">
                      Configure Form Fields & QR Purpose Blacklist
                    </Label>
                    <p className="text-sm text-gray-600 mb-4">
                      Click on any field below to expand and configure which QR scan purposes should NOT be allowed to modify that field or its options (for dropdowns)
                    </p>
                    <FormBuilder
                      eventId={editingEvent.id}
                      initialSchema={{
                        name: formSchemas.find(s => s.schemaId === editingEvent.selectedSchemaId)?.name || '',
                        fields: editingEvent.formSchema,
                        passes: editingEvent.passes || [],
                        groups: editingEvent.groups || [],
                        formLogoUrl: editingEvent.formLogoUrl,
                        formBackgroundUrl: editingEvent.formBackgroundUrl
                      }}
                      onSave={(updatedSchema) => {
                        setEditingEvent(prev => ({
                          ...prev,
                          formSchema: updatedSchema.fields,
                          formLogoUrl: updatedSchema.formLogoUrl,
                          formBackgroundUrl: updatedSchema.formBackgroundUrl,
                          passes: updatedSchema.passes || prev.passes || [],
                          groups: updatedSchema.groups || prev.groups || [],
                          passesLoaded: prev.passesLoaded || (updatedSchema.passes || []).length > 0,
                        }));
                      }}
                      hideMainSaveButton={true}
                      autoSaveOnFieldEdit={true}
                      className="bg-white"
                    />
                  </div>
                </div>
              )}
              
              {/* Selected Schema Preview for New Events */}
              {!editingEvent && newEvent.formSchema?.length > 0 && (
                <div className="mt-6">
                  <Label className="text-md font-medium">Selected Form Fields</Label>
                  <p className="text-sm text-gray-600 mb-2">
                    Note: QR purpose blacklist configuration will be available after creating the event
                  </p>
                  <div className="mt-2 space-y-2">
                    {newEvent.formSchema.map((field, index) => (
                      <div key={index} className="flex items-center space-x-2 p-3 border rounded-lg bg-gray-50">
                        <div className="flex-1">
                          <span className="font-medium">{field.label}</span>
                          <span className="text-sm text-gray-500 ml-2">
                            ({field.type}) {field.required ? 'Required' : 'Optional'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => {
                  if (editingEvent) {
                    setEditingEvent(null);
                  } else {
                    resetForm();
                    setIsCreating(false);
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={editingEvent ? handleUpdateEvent : handleCreateEvent}
                disabled={editingEvent ? updateEventMutation.isPending : createEventMutation.isPending}
              >
                {editingEvent ? 
                  (updateEventMutation.isPending ? 'Updating...' : 'Update Event') :
                  (createEventMutation.isPending ? 'Creating...' : 'Create Event')
                }
              </Button>
            </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Events List */}
      <div className="space-y-4">
        {filteredEvents.length === 0 && !isLoading ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? 'No events found' : 'No events yet'}
              </h3>
              <p className="text-gray-600 mb-4">
                {searchTerm ? 'Try adjusting your search term.' : 'Create your first event to get started.'}
              </p>
              {!searchTerm && (
                <Button onClick={() => setIsCreating(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Event
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredEvents.map((event) => (
            <Card key={event.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{event.name}</h3>
                      <div className="flex items-center space-x-2">
                        <div className={`px-2 py-1 rounded text-sm ${
                          event.eventType === 'free' ? 'bg-green-100 text-green-700' :
                          event.eventType === 'paid' ? 'bg-blue-100 text-blue-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {event.eventType === 'free' ? 'Free' :
                           (event.eventType === 'paid' || event.eventType === 'PAID') ? `${getCurrencySymbol(event.currency)}${event.price ?? '0'}` :
                           'Limited'}
                        </div>
                        {(event.eventType === 'paid' || event.eventType === 'PAID') && (
                          <Link to={`/admin/events/${eventRef(event)}/payment-settings`}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={isPaymentSettingsConfigured(event.id) ? 'text-green-600' : 'text-orange-600'}
                              title="Payment settings"
                            >
                              <CreditCard className="h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-4">{event.description}</p>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        <div>
                          <p className="font-medium">Date</p>
                          <p>{formatDate(event.eventDate)}</p>
                          {event.eventEndDate && (
                            <p className="text-xs">to {formatDate(event.eventEndDate)}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-500">
                        <Users className="h-4 w-4 mr-2 text-gray-400" />
                        <div>
                          <p className="font-medium">Registrations</p>
                          <p>{event.registered || 0} / {event.capacity || 0}</p>
                        </div>
                      </div>
                    </div>

                    {event.location && (
                      <div className="flex items-center text-sm text-gray-500 mb-4">
                        <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                        <p>{event.location}</p>
                      </div>
                    )}

                    <div className="flex items-center space-x-2 pt-4 border-t">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditEvent(event)}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Edit Event</p>
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => cloneEventMutation.mutate(event.id)}
                              disabled={cloneEventMutation.isPending}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Clone event</p>
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyShareLink(event.id)}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Copy Registration Link</p>
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setGeneratingLinkForEvent(event)}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              <LinkIcon className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Generate Pre-filled Link</p>
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link to={`/register/${eventRef(event)}`} target="_blank">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Preview Registration Form</p>
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link to={`/admin/events/${eventRef(event)}/settings`}>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-gray-600 hover:text-gray-900"
                                aria-label="Event settings"
                              >
                                <Settings className="h-4 w-4" />
                              </Button>
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Event Settings</p>
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link to={`/admin/events/${eventRef(event)}/registrations`}>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <Users className="h-4 w-4" />
                              </Button>
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>View Registrations</p>
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingQRPurposes(event)}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              <QrCode className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Configure QR Scan Purposes</p>
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingEmailConfig(event)}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Configure Email Notifications</p>
                          </TooltipContent>
                        </Tooltip>

                        {event.lockerEnabled && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingLockers(event)}
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <Lock className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Configure Lockers</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      {/* Generate Link Modal */}
      {generatingLinkForEvent && (
        <GenerateLinkModal
          eventId={generatingLinkForEvent.id}
          formSchema={generatingLinkForEvent.formSchema}
          onClose={() => setGeneratingLinkForEvent(null)}
        />
      )}

      {/* QR Scan Purposes Modal */}
      {editingQRPurposes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <Card className="border-0 shadow-none">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">
                    QR Scan Purposes - {editingQRPurposes.name}
                  </CardTitle>
                  <Button variant="outline" onClick={() => setEditingQRPurposes(null)}>
                    Cancel
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <QRScanPurposeManager eventId={editingQRPurposes.id} />
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Payment Settings Modal */}
      {editingPaymentSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <Card className="border-0 shadow-none">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">
                    Payment Settings - {events.find(e => e.id === editingPaymentSettings)?.name}
                  </CardTitle>
                  <Button variant="outline" onClick={handleCancelPaymentSettings}>
                    Cancel
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <PaymentSettingsForm
                  initialSettings={paymentSettingsMap?.[editingPaymentSettings]}
                  onSave={handleSavePaymentSettings}
                  onCancel={handleCancelPaymentSettings}
                  isLoading={savePaymentSettingsMutation.isPending}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Email Configuration Modal */}
      {editingEmailConfig && (
        <EmailConfigModal
          eventId={editingEmailConfig.id}
          eventName={editingEmailConfig.name}
          onClose={() => setEditingEmailConfig(null)}
        />
      )}

      {/* Locker Configuration Modal */}
      {editingLockers && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <Card className="border-0 shadow-none">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">
                    Lockers - {editingLockers.name}
                  </CardTitle>
                  <Button variant="outline" onClick={() => setEditingLockers(null)}>
                    Close
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <LockerConfigManager eventId={editingLockers.id} />
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventManagement;
